<template>
  <nav v-if="expansions.length > 0 || selected" class="query-expansions">
    <EBaseCarousel>
      <template #previous-control="{ getCurrentScrollPosition, visible }">
        <button
          v-show="visible"
          type="button"
          class="query-expansions__control query-expansions__control--previous"
          data-test-id="query-expansions-control-previous"
          @click="onPrevious(getCurrentScrollPosition)"
        >
          <EIcon icon="chevron-left" size="s" />
        </button>
      </template>
      <ul
        ref="scroll-list"
        class="query-expansions__list"
        data-test-id="query-expansions-list"
      >
        <EChip
          v-if="selected"
          :url="getExpansionUrl(selected, true)"
          :thumbnail-url="getThumbnailUrl(selected)"
          :text="formatExpansionText(selected)"
          :selected="true"
          class="query-expansions__item"
          data-test-id="query-expansions-item-selected"
        />
        <EChip
          v-for="(expansion, index) in expansions"
          :key="index"
          :url="getExpansionUrl(expansion)"
          :thumbnail-url="getThumbnailUrl(expansion)"
          :text="formatExpansionText(expansion)"
          :selected="false"
          class="query-expansions__item"
          data-test-id="query-expansions-item"
        />
      </ul>
      <template #next-control="{ getCurrentScrollPosition, visible }">
        <button
          v-show="visible"
          type="button"
          class="query-expansions__control query-expansions__control--next"
          data-test-id="query-expansions-control-next"
          @click="onNext(getCurrentScrollPosition)"
        >
          <EIcon icon="chevron-right" size="s" />
        </button>
      </template>
    </EBaseCarousel>
  </nav>
</template>

<script>
import EBaseCarousel from '~~/common/vue/components/base-carousel';
import EIcon from '~~/common/vue/components/icon';
import EChip, { THUMBNAIL_SIZE } from '~/components/chip';
import {
  formatQueryExpansionText,
  encodeQueryExpansion,
  decodeQueryExpansion,
} from '~/utils/query-expansions';

export const CAROUSEL_JUMP_ITEMS = 5;

const scrollOptions = { behavior: 'smooth', block: 'nearest', inline: 'start' };

export default {
  name: 'EQueryExpansions',
  components: {
    EBaseCarousel,
    EChip,
    EIcon,
  },
  props: {
    expansions: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    selected: null,
  }),
  created() {
    if (this.$route.query.expansion) {
      try {
        this.selected = decodeQueryExpansion(this.$route.query.expansion);
      } catch (err) {
        this.selected = null;
      }
    }
  },
  methods: {
    getThumbnailUrl({ thumbnailURL }) {
      try {
        const url = new URL(thumbnailURL);
        url.searchParams.set('h', THUMBNAIL_SIZE * 2);
        return url.toString();
      } catch (err) {
        return '';
      }
    },
    formatExpansionText({ text = '', displayText = '' }) {
      return formatQueryExpansionText(text, displayText);
    },
    getExpansionUrl({ text, displayText, thumbnailURL }, isSelected = false) {
      try {
        const { q, originalQuery, mocked } = this.$route.query;
        const query = {
          // swaps values of q and originalQuery
          q: originalQuery,
        };

        if (mocked) {
          query.mocked = mocked;
        }

        if (!isSelected) {
          query.q = text;
          query.expansion = encodeQueryExpansion({ text, displayText, thumbnailURL });
          query.originalQuery = q;
        }

        return this.$router.resolve({ query }).href;
      } catch (err) {
        return this.$route.fullPath;
      }
    },
    scrollCarousel(currentScrollPosition, direction) {
      const directionMultiplier = direction === 'forward' ? 1 : -1;
      const list = this.$refs['scroll-list'];
      if (!list) {
        return;
      }

      const items = Array.from(list.children);
      const firstVisibleItemIndex = items.findIndex(
        (item) => item.offsetLeft >= currentScrollPosition);
      const jumpIndex = firstVisibleItemIndex + directionMultiplier * CAROUSEL_JUMP_ITEMS;
      const nextVisibleItemIndex = Math.max(0, Math.min(jumpIndex, items.length));
      // in case the item list inside the carousel has padding, we have to scroll
      // to the beginning of the list itself, rather than the first item
      if (nextVisibleItemIndex === 0) {
        this.scrollCarouselToBeginning();
        return;
      }
      items[nextVisibleItemIndex].scrollIntoView(scrollOptions);
    },
    scrollCarouselToBeginning() {
      const list = this.$refs['scroll-list'];
      if (!list) {
        return;
      }
      list.scrollIntoView(scrollOptions);
    },
    onPrevious(getCurrentScrollPosition) {
      this.scrollCarousel(getCurrentScrollPosition(), 'back');
    },
    onNext(getCurrentScrollPosition) {
      this.scrollCarousel(getCurrentScrollPosition(), 'forward');
    },
    onSelect(expansion) {
      this.selected = expansion;
    },
  },
};
</script>

<style lang="scss" scoped>
$button-gradient-color-stops: $transparent-white, rgba($white, 0.8) 42%, $white 74%;

.query-expansions {
  margin-top: -$space-1s;
  position: relative;
  width: 100%;
}

.query-expansions__list {
  align-items: flex-start;
  display: flex;
  flex-shrink: 0;
  list-style-type: none;
  margin: $space-1s 0;
  padding: 0 $space-s;
  width: max-content;

  @include desktop {
    padding: 0 $space-m;
  }
}

.query-expansions__item {
  flex-shrink: 0;
  margin: 0;

  & + & {
    margin-left: $space-1s;
  }
}

.query-expansions__control {
  appearance: none;
  background: none;
  align-items: center;
  border: none;
  color: $gray-40;
  cursor: pointer;
  height: 100%;
  padding: 0 $space-2s;
  top: 0;
  // non-standard size defined in design
  width: 60px;
  display: none;
  position: absolute;
  z-index: $z-index-l;
  outline: none;
  transition: color $timing-2s $easing;

  &:hover,
  &:focus {
    color: $gray-60;
  }

  @include desktop {
    display: flex;
  }
}

.query-expansions__control--previous {
  background: linear-gradient(to left, $button-gradient-color-stops);
  justify-content: flex-start;
  left: 0;
  padding-left: $space-l;
}

.query-expansions__control--next {
  background: linear-gradient(to right, $button-gradient-color-stops);
  justify-content: flex-end;
  right: 0;
  padding-right: $space-l;
}
</style>
